"use client";

import { c as _c } from "react/compiler-runtime";
import useCreateModalState from "@/hooks/useCreateModalState";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createBreakpoint } from "react-use";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import type { ApiDrop } from "../../generated/models/ApiDrop";
import { DropSize } from "../../helpers/waves/drop.helpers";
import { useSidebarState } from "../../hooks/useSidebarState";
import { commonApiFetch } from "../../services/api/common-api";
import { useAuth } from "../auth/Auth";
import BrainDesktopDrop from "../brain/BrainDesktopDrop";
import WebBrainLeftSidebar from "../brain/left-sidebar/web/WebLeftSidebar";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import BrainRightSidebar, { SidebarTab } from "../brain/right-sidebar/BrainRightSidebar";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import CreateWaveModal from "../waves/create-wave/CreateWaveModal";
import { WaveChatScrollProvider } from "@/contexts/wave/WaveChatScrollContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
const useBreakpoint = createBreakpoint({
  XL: 1400,
  LG: 1024,
  S: 0
});
interface WavesMessagesWrapperProps {
  readonly children: ReactNode;
  readonly defaultPath?: string | undefined; // "/waves" or "/messages"
  readonly showLeftSidebar?: boolean | undefined;
  readonly allowRightSidebar?: boolean | undefined;
  readonly allowDropOverlay?: boolean | undefined;
}
const WavesMessagesWrapper: React.FC<WavesMessagesWrapperProps> = t0 => {
  const $ = _c(61);
  const {
    children,
    defaultPath: t1,
    showLeftSidebar: t2,
    allowRightSidebar: t3,
    allowDropOverlay: t4
  } = t0;
  const defaultPath = t1 === undefined ? "/waves" : t1;
  const showLeftSidebar = t2 === undefined ? true : t2;
  const allowRightSidebar = t3 === undefined ? true : t3;
  const allowDropOverlay = t4 === undefined ? true : t4;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const {
    contentContainerStyle
  } = useLayout();
  const {
    isRightSidebarOpen,
    closeRightSidebar
  } = useSidebarState();
  const {
    connectedProfile
  } = useAuth();
  const {
    isWaveModalOpen,
    close
  } = useCreateModalState();
  const [sidebarTab, setSidebarTab] = useState(SidebarTab.ABOUT);
  let t5;
  let waveId;
  if ($[0] !== pathname || $[1] !== searchParams) {
    const rawDropId = searchParams.get("drop") ?? undefined;
    waveId = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? undefined;
    t5 = rawDropId && /^[a-zA-Z0-9-_]+$/.test(rawDropId) ? rawDropId : undefined;
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t5;
    $[3] = waveId;
  } else {
    t5 = $[2];
    waveId = $[3];
  }
  const dropId = t5;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  const isMobile = breakpoint === "S";
  const isLargeDesktop = breakpoint === "XL";
  let t6;
  let t7;
  if ($[4] !== closeRightSidebar || $[5] !== isRightSidebarOpen || $[6] !== waveId) {
    t6 = () => {
      if (!waveId && isRightSidebarOpen) {
        closeRightSidebar();
      }
    };
    t7 = [waveId, isRightSidebarOpen, closeRightSidebar];
    $[4] = closeRightSidebar;
    $[5] = isRightSidebarOpen;
    $[6] = waveId;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== allowRightSidebar || $[10] !== closeRightSidebar || $[11] !== isRightSidebarOpen) {
    t8 = () => {
      if (!allowRightSidebar && isRightSidebarOpen) {
        closeRightSidebar();
      }
    };
    t9 = [allowRightSidebar, isRightSidebarOpen, closeRightSidebar];
    $[9] = allowRightSidebar;
    $[10] = closeRightSidebar;
    $[11] = isRightSidebarOpen;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[14] !== effectiveDropId) {
    t10 = [QueryKey.DROP, {
      drop_id: effectiveDropId
    }];
    t11 = async () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return await commonApiFetch({
        endpoint: `drops/${effectiveDropId}`
      });
    };
    $[14] = effectiveDropId;
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  const t12 = allowDropOverlay && !!effectiveDropId;
  let t13;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t12) {
    t13 = {
      queryKey: t10,
      queryFn: t11,
      placeholderData: keepPreviousData,
      enabled: t12
    };
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const {
    data: drop,
    error: dropError
  } = useQuery(t13);
  let t14;
  if ($[21] !== beginClosingDrop || $[22] !== defaultPath || $[23] !== dropId || $[24] !== pathname || $[25] !== router || $[26] !== searchParams) {
    t14 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
        markDropCloseNavigation();
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("drop");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || defaultPath;
      router.replace(newUrl, {
        scroll: false
      });
    };
    $[21] = beginClosingDrop;
    $[22] = defaultPath;
    $[23] = dropId;
    $[24] = pathname;
    $[25] = router;
    $[26] = searchParams;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const onDropClose = t14;
  drop?.id;
  let t15;
  if ($[28] !== drop?.id || $[29] !== effectiveDropId) {
    t15 = effectiveDropId && drop?.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[28] = drop?.id;
    $[29] = effectiveDropId;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const isDropOpen = Boolean(t15);
  const shouldShowLeftSidebar = showLeftSidebar && (!isMobile || !waveId);
  const shouldShowMainContent = !isMobile || waveId !== undefined;
  const shouldShowDropOverlay = allowDropOverlay && isDropOpen && drop !== undefined && shouldShowMainContent;
  const shouldShowRightSidebar = Boolean(allowRightSidebar && isRightSidebarOpen && waveId && !isDropOpen);
  const canInlineRight = !isMobile && (isLargeDesktop || breakpoint === "LG");
  let rightVariant = null;
  if (shouldShowRightSidebar) {
    rightVariant = canInlineRight ? "inline" : "overlay";
  }
  if (dropError && effectiveDropId) {
    console.error("Failed to load drop:", dropError);
  }
  let t16;
  if ($[31] !== rightVariant || $[32] !== shouldShowLeftSidebar) {
    t16 = shouldShowLeftSidebar && <WebBrainLeftSidebar isCollapsed={rightVariant === "inline"} />;
    $[31] = rightVariant;
    $[32] = shouldShowLeftSidebar;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== children || $[35] !== drop || $[36] !== onDropClose || $[37] !== shouldShowDropOverlay || $[38] !== shouldShowMainContent) {
    t17 = shouldShowMainContent && <div className="tw-flex tw-h-full tw-min-w-0 tw-flex-grow tw-flex-col tw-border-y-0 tw-border-l-0 tw-border-r tw-border-solid tw-border-iron-800">{children}{shouldShowDropOverlay && <div className="tw-fixed tw-inset-y-0 tw-left-[var(--left-rail,0px)] tw-right-0 tw-z-[1010] lg:tw-absolute lg:tw-inset-0 lg:tw-z-[1010]"><BrainDesktopDrop drop={{
          type: DropSize.FULL,
          ...drop,
          stableKey: drop.id,
          stableHash: drop.id
        }} onClose={onDropClose} /></div>}</div>;
    $[34] = children;
    $[35] = drop;
    $[36] = onDropClose;
    $[37] = shouldShowDropOverlay;
    $[38] = shouldShowMainContent;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== rightVariant || $[41] !== sidebarTab || $[42] !== waveId) {
    t18 = rightVariant === "inline" && <div className="tw-hidden tw-flex-shrink-0 tw-pl-6 tw-pt-2 lg:tw-block"><BrainRightSidebar variant="inline" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} /></div>;
    $[40] = rightVariant;
    $[41] = sidebarTab;
    $[42] = waveId;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== contentContainerStyle || $[45] !== t16 || $[46] !== t17 || $[47] !== t18) {
    t19 = <div className="tw-relative tw-flex tw-flex-col"><div className="tw-relative tw-flex tw-flex-grow"><div className="tw-relative tw-mx-auto tw-flex tw-w-full tw-max-w-full tw-flex-grow"><div className="tw-relative tw-flex tw-w-full tw-overflow-hidden" style={contentContainerStyle}>{t16}{t17}{t18}</div></div></div></div>;
    $[44] = contentContainerStyle;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== rightVariant || $[50] !== sidebarTab || $[51] !== waveId) {
    t20 = rightVariant === "overlay" && <BrainRightSidebar variant="overlay" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} />;
    $[49] = rightVariant;
    $[50] = sidebarTab;
    $[51] = waveId;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== close || $[54] !== connectedProfile || $[55] !== isWaveModalOpen) {
    t21 = connectedProfile && <CreateWaveModal isOpen={isWaveModalOpen} onClose={close} profile={connectedProfile} />;
    $[53] = close;
    $[54] = connectedProfile;
    $[55] = isWaveModalOpen;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t19 || $[58] !== t20 || $[59] !== t21) {
    t22 = <WaveChatScrollProvider>{t19}{t20}{t21}</WaveChatScrollProvider>;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  return t22;
};
export default WavesMessagesWrapper;